import {CompanyPlanCurrentDto, PlanChoiceDto} from '../../shared/dtos/company-plan.dto';
import {
  allowance,
  annualFor,
  bigPrice,
  BillingCycle,
  count,
  PlanCatalog,
  planMoney,
  priceFor,
  rateMoney,
  topTier,
  upgradeCost,
  upgradeEstimates,
} from '../../shared/pricing/plan-pricing';

/*
 * SQ-415: what a pick on the plan chooser means, the same rules the server
 * charges by (CompanyPlanService) and Android's PlanScreen shows:
 * monthly up is charged now, monthly down from renewal (this month keeps
 * what was paid), monthly to yearly starts the year today, yearly up costs
 * the months left at the upgrade discount, and yearly down or to monthly
 * waits for renewal.
 */

/** A pick: a tier, or a total above the top tier (a month's, or a year's pool). */
export interface PlanPick {
  cycle: BillingCycle;
  tier?: number;
  total?: number;
}

export const pickPerMonth = (pick: PlanPick) =>
  pick.tier ?? (pick.cycle === 'annual' ? pick.total! / 12 : pick.total!);

export const pickAllowance = (pick: PlanPick) => pick.tier != null ? allowance(pick.tier, pick.cycle === 'annual') : pick.total!;

export const pickPrice = (catalog: PlanCatalog, pick: PlanPick) =>
  pick.tier != null
    ? (pick.cycle === 'annual' ? annualFor(catalog, pick.tier) : priceFor(catalog, pick.tier))
    : bigPrice(catalog, pick.total!, pick.cycle === 'annual');

export const toChoice = (pick: PlanPick): PlanChoiceDto =>
  pick.tier != null ? {cycle: pick.cycle, tier: pick.tier} : {cycle: pick.cycle, total: pick.total};

/** The lowest total the "more than" option offers: one step past the top tier. */
export const firstBigTotal = (catalog: PlanCatalog, cycle: BillingCycle) =>
  (cycle === 'annual' ? topTier(catalog) * 12 : topTier(catalog)) + catalog.bigStep;

export type PlanChange =
  | {kind: 'none'}
  | {kind: 'checkout'; label: string; note: string}
  | {kind: 'same'; label: string}
  | {kind: 'locked'; note: string}
  | {kind: 'change'; label: string; note: string; cost?: number; added?: number};

export const renewsOn = (plan: CompanyPlanCurrentDto) =>
  plan.periodEnd
    ? new Date(plan.periodEnd).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'})
    : 'renewal';

/** True when the current plan is yearly: smaller plans and monthly ones wait for renewal. */
export const isLocked = (plan: CompanyPlanCurrentDto | null, pick: PlanPick) => {
  if (!plan || plan.cycle !== 'annual') return false;
  if (pick.cycle === 'monthly') return true;
  return pickAllowance(pick) < allowance(plan.perMonth, true);
};

export function describeChange(catalog: PlanCatalog, plan: CompanyPlanCurrentDto | null, pick: PlanPick | null): PlanChange {
  if (!pick) return {kind: 'none'};
  const annual = pick.cycle === 'annual';
  const target = pickAllowance(pick);
  const term = annual ? `${count(target)} a year` : `${count(target)} a month`;

  if (!plan) {
    return {
      kind: 'checkout',
      label: `Continue to payment: ${planMoney(pickPrice(catalog, pick))} ${annual ? 'a year' : 'a month'}`,
      note: 'Stripe, our payment processor, takes the card on its own page and brings you back here.',
    };
  }

  const currentAnnual = plan.cycle === 'annual';
  const current = allowance(plan.perMonth, currentAnnual);
  if (annual === currentAnnual && target === current) return {kind: 'same', label: 'This is your plan'};

  if (currentAnnual && !annual) {
    return {kind: 'locked', note: `Your year is already paid for. Monthly plans are available when it renews on ${renewsOn(plan)}.`};
  }
  if (currentAnnual) {
    if (target < current) {
      return {kind: 'locked', note: `Your year is already paid for, so the plan can only go up until it renews on ${renewsOn(plan)}.`};
    }
    const to = pickPerMonth(pick);
    const cost = upgradeCost(catalog, plan.perMonth, to, plan.monthsLeft);
    const added = upgradeEstimates(plan.perMonth, to, plan.monthsLeft);
    const off = Math.round(catalog.annualUpgradeDiscount * 100);
    const full = Math.round(catalog.annualDiscount * 100);
    return {
      kind: 'change',
      label: `Upgrade for ${planMoney(cost)}`,
      cost,
      added,
      note: `${count(added)} more estimates for the ${plan.monthsLeft} month${plan.monthsLeft === 1 ? '' : 's'} left in your year, ` +
        `at ${rateMoney(added > 0 ? cost / added : 0)} each. You only pay for the months you have left. Estimates added part way ` +
        `through get ${off}% off instead of the ${full}% for committing up front, and your renewal goes back to the full ${full}% ` +
        'at the new tier.',
    };
  }
  if (annual) {
    return {
      kind: 'change',
      label: `Switch to ${term} for ${planMoney(pickPrice(catalog, pick))}`,
      note: 'Your year starts today and is charged now. What is left of this month is credited against it.',
    };
  }
  if (target > current) {
    return {
      kind: 'change',
      label: `Switch to ${term}`,
      note: 'The difference for the rest of this month is charged now, and the new allowance starts at once.',
    };
  }
  return {
    kind: 'change',
    label: `Switch to ${term}`,
    note: `From ${renewsOn(plan)} you pay ${planMoney(pickPrice(catalog, pick))} a month. ` +
      `This month keeps the ${count(current)} estimates you already paid for.`,
  };
}
